import { z } from 'zod'

import {
  accept,
  isPlayersTurn,
  reject,
  type ActionContext,
  type GameEngine,
  type PlaySummary,
  type Player,
  type TurnState,
} from '../engine.js'
import type { Rng } from '../rng.js'
import type { TeddyKey } from '../teddies.js'
import { FLUENT_MS } from './maths-adaptive.js'
import { levelOf, MAX_LEVEL, MIN_LEVEL, type SkillLevels } from './maths-skills.js'
import { TRIES_PER_QUESTION } from './maths.js'
import { buildRiddle, type Riddle } from './riddle-puzzle.js'
import { overallRiddleLevel, RIDDLE_SKILLS, shapeForLevel } from './riddle-skills.js'
import { clueSentence, questionSentence } from './riddle-words.js'

/**
 * Rocky's Riddles.
 *
 * The first game in the roster that asks her to **deduce** — to reason from
 * what she is told to something that must be true. Everything else exercises
 * arithmetic, spelling, recall or reading; none of them has this shape, and it
 * is the shape most of school eventually turns out to be.
 *
 * Rocky is a dog, so he is the detective.
 *
 * Its levels are its own. Reading a row of clues and knowing your times tables
 * are unrelated, and one must never move the other.
 */
export const DEFAULT_RIDDLE_ROUND = 6

export const riddleAction = z.object({
  type: z.literal('answer'),
  /** The seat she picked, from one, as the key she pressed. */
  value: z.number().int().min(1).max(9),
})

export type RiddleAction = z.infer<typeof riddleAction>

export interface RiddleConfig {
  readonly levels?: SkillLevels
  readonly roundLength?: number
  readonly startedAt?: number
}

export interface RiddleAttempt {
  readonly skillKey: string
  readonly prompt: string
  readonly expected: number
  readonly given: number
  readonly correct: boolean
  readonly elapsedMs: number
  readonly playerId: string
}

export interface RiddleState {
  readonly queue: readonly Riddle[]
  readonly levels: SkillLevels
  readonly roundLength: number
  readonly attempts: readonly RiddleAttempt[]
  readonly lastAnswer: RiddleAttempt | null
  /** The row the last answer was about, so "it was Hoppity" can be said. */
  readonly lastAnswerTeddies: readonly TeddyKey[]
  readonly lastAnswerHadMoreGoes: boolean
  readonly correctCount: number
  readonly status: 'playing' | 'finished'
  readonly turn: TurnState
  readonly startedAt: number
  readonly askedAt: number
  readonly triesOnCurrent: number
}

export interface RiddleView {
  /** In seating order, front first. Drawn, so she can see it while she thinks. */
  readonly teddies: readonly TeddyKey[]
  /** Already written out. The words are decided once, in one place. */
  readonly clues: readonly string[]
  readonly prompt: string | null
  readonly correct: number
  readonly roundLength: number
  readonly status: RiddleState['status']
  readonly currentPlayerId: string | null
  readonly lastAnswer:
    | (Pick<RiddleAttempt, 'prompt' | 'expected' | 'given' | 'correct'> & {
        readonly moreGoes: boolean
        /** The row it was asked about, which is not the row on screen now. */
        readonly teddies: readonly TeddyKey[]
      })
    | null
  readonly triesLeft: number
  readonly wrongAnswers: number
}

/**
 * What a client is allowed to see.
 *
 * The deal never leaves the server in a room. A riddle whose answer travelled
 * with it would be a riddle a socket payload could be read for, which is the
 * same rule Hangman's secret word follows.
 */
export function toRiddleView(state: RiddleState): RiddleView {
  const current = state.queue[0]
  const row = current ? { teddies: current.teddies, categories: current.categories } : null

  return {
    teddies: current?.teddies ?? [],
    clues: current && row ? current.clues.map((clue) => clueSentence(clue, row)) : [],
    prompt: current && row ? questionSentence(current.question, row) : null,
    correct: state.correctCount,
    roundLength: state.roundLength,
    status: state.status,
    currentPlayerId: state.turn.currentPlayerId,
    lastAnswer: state.lastAnswer
      ? {
          prompt: state.lastAnswer.prompt,
          expected: state.lastAnswer.expected,
          given: state.lastAnswer.given,
          correct: state.lastAnswer.correct,
          moreGoes: state.lastAnswerHadMoreGoes,
          teddies: state.lastAnswerTeddies,
        }
      : null,
    triesLeft: Math.max(0, TRIES_PER_QUESTION - state.triesOnCurrent),
    wrongAnswers: state.attempts.filter((attempt) => !attempt.correct).length,
  }
}

/** Moves a level after an answer, the same way Turtley's does. */
function applyRiddleAnswer(
  levels: SkillLevels,
  skillKey: string,
  correct: boolean,
  elapsedMs: number,
): SkillLevels {
  const level = levelOf(levels, skillKey)

  // Right and reasonably quick moves her on; right but slow holds; wrong steps
  // back. A riddle worked out slowly is still worked out — and this one is
  // meant to be worked out slowly, which is why the fluent window is the
  // generous one rather than Monkey's.
  const next = correct
    ? elapsedMs <= FLUENT_MS * 4
      ? Math.min(MAX_LEVEL, level + 1)
      : level
    : Math.max(MIN_LEVEL, level - 1)

  return { ...levels, [skillKey]: next }
}

/** Only the skills this round practised, for writing levels back. */
export function riddleLevelsTouched(state: RiddleState): SkillLevels {
  const touched = new Set(state.attempts.map((attempt) => attempt.skillKey))

  return Object.fromEntries(
    [...touched].map((skillKey) => [skillKey, levelOf(state.levels, skillKey)]),
  )
}

/**
 * Puts a missed riddle back, a couple of places later.
 *
 * The same idea as Monkey's, and more literally true here: coming straight
 * back to a riddle she has just been shown the answer to would test nothing
 * but whether she remembers a name from ten seconds ago.
 */
function requeue(queue: readonly Riddle[], riddle: Riddle): readonly Riddle[] {
  const rest = queue.slice(1)
  const at = Math.min(2, rest.length)

  return [...rest.slice(0, at), riddle, ...rest.slice(at)]
}

export const riddlesEngine: GameEngine<RiddleConfig, RiddleState, RiddleAction> = {
  key: 'riddles',
  actionSchema: riddleAction,

  createInitialState(config: RiddleConfig, players: readonly Player[], rng: Rng): RiddleState {
    const levels = config.levels ?? {}
    const roundLength = config.roundLength ?? DEFAULT_RIDDLE_ROUND

    // Dealt whole, up front, from where she actually stands — so the reducer
    // needs no randomness of its own and a round replays exactly from its seed.
    const shape = shapeForLevel(overallRiddleLevel(levels))
    const queue = Array.from({ length: roundLength }, () => buildRiddle(shape, rng))

    return {
      queue,
      levels,
      roundLength,
      attempts: [],
      lastAnswer: null,
      lastAnswerTeddies: [],
      lastAnswerHadMoreGoes: false,
      correctCount: 0,
      status: 'playing',
      turn: { players, currentPlayerId: players[0]?.id ?? null },
      startedAt: config.startedAt ?? 0,
      askedAt: config.startedAt ?? 0,
      triesOnCurrent: 0,
    }
  },

  reduce(state, action, context: ActionContext) {
    if (state.status !== 'playing') return reject(state, 'game-over')
    if (!isPlayersTurn(state.turn, context.playerId)) return reject(state, 'not-your-turn')

    const current = state.queue[0]
    if (!current) return reject(state, 'game-over')

    // A seat that is not in this row is a client sending nonsense, not a wrong
    // answer. Recording it would put a question she was never asked in front
    // of a parent.
    if (action.value > current.teddies.length) return reject(state, 'invalid-action')

    const elapsedMs = Math.max(0, context.now - state.askedAt)
    const correct = action.value === current.answer
    const row = { teddies: current.teddies, categories: current.categories }

    const attempt: RiddleAttempt = {
      skillKey: current.skillKey,
      // Written out, because a parent reading this months later needs the
      // riddle and not a seat number. The clues go with it: "Who took the
      // plum?" alone says nothing about what she had to work with.
      prompt: [
        ...current.clues.map((clue) => clueSentence(clue, row)),
        questionSentence(current.question, row),
      ].join(' '),
      expected: current.answer,
      given: action.value,
      correct,
      elapsedMs,
      playerId: context.playerId,
    }

    const attempts = [...state.attempts, attempt]
    const tries = correct ? 0 : state.triesOnCurrent + 1
    // The same three goes as Monkey's and Turtley's, for the same reason:
    // being told to try again is how somebody finds an answer themselves.
    const tryAgain = !correct && tries < TRIES_PER_QUESTION

    const queue = correct
      ? state.queue.slice(1)
      : tryAgain
        ? state.queue
        : requeue(state.queue, current)

    const finished =
      queue.length === 0 || attempts.length >= state.roundLength * (TRIES_PER_QUESTION + 1)

    return accept({
      ...state,
      queue,
      levels: applyRiddleAnswer(state.levels, current.skillKey, correct, elapsedMs),
      attempts,
      lastAnswer: attempt,
      lastAnswerTeddies: current.teddies,
      lastAnswerHadMoreGoes: tryAgain,
      correctCount: correct ? state.correctCount + 1 : state.correctCount,
      status: finished ? 'finished' : 'playing',
      askedAt: context.now,
      triesOnCurrent: tryAgain ? tries : 0,
      turn: { ...state.turn, currentPlayerId: state.turn.currentPlayerId },
    })
  },

  isTerminal(state) {
    return state.status === 'finished'
  },

  summarise(state, endedAt): PlaySummary {
    return {
      gameKey: 'riddles',
      result: state.correctCount === state.roundLength ? 'won' : 'abandoned',
      durationMs: Math.max(0, endedAt - state.startedAt),
      difficulty: `level ${Math.round(overallRiddleLevel(state.levels))}`,
    }
  },
}

export { RIDDLE_SKILLS }
